import { Controller } from "@hotwired/stimulus"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  deactivate(event) {
    const button = this.startAction(event)
    if (!button) return

    const row = button.closest("tr")
    const memberName = this.memberName(row)
    if (!window.confirm(`Deactivate ${memberName}? They will lose access to this team.`)) return

    this.submit({
      button: button,
      url: row.dataset.deactivateUrl,
      method: "POST",
      errorPrefix: "Failed to deactivate member",
      onSuccess: () => this.removeRow(row)
    })
  }

  // Members who have never been invited are deleted outright: nothing has reached them, so there
  // is no history worth keeping and the admin expects a mistyped row to simply disappear.
  remove(event) {
    const button = this.startAction(event)
    if (!button) return

    const row = button.closest("tr")
    const memberName = this.memberName(row)
    if (!window.confirm(`Remove ${memberName}? They have not been invited, so this cannot be undone.`)) return

    // Reload rather than drop the row: removing a staged member also changes the
    // awaiting-invitation count and the Send Invitations button rendered in the toolbar.
    this.submit({
      button: button,
      url: row.dataset.removeUrl,
      method: "DELETE",
      errorPrefix: "Failed to remove member",
      onSuccess: () => window.location.reload()
    })
  }

  async submit({ button, url, method, errorPrefix, onSuccess }) {
    if (!url) return

    button.disabled = true

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        onSuccess()
        return
      }

      const message = data?.errors?.join(", ") || `HTTP ${response.status}`
      showHubTeamToast(`${errorPrefix}: ${message}`, { variant: "error" })
    } catch (error) {
      console.error("Error updating company member:", error)
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    }

    button.disabled = false
  }

  startAction(event) {
    event.preventDefault()

    const button = event.currentTarget
    if (button.disabled || !button.closest("tr")) return null

    return button
  }

  memberName(row) {
    return row.querySelector(".hub-team-member-name-text")?.textContent?.trim() || "this member"
  }

  removeRow(row) {
    if (!row) return

    const table = row.closest("[data-controller~='data-table']")
    row.remove()

    this.updateMemberCount(-1)

    if (table) {
      const remainingRows = table.querySelectorAll("tbody tr")
      const emptyState = table.querySelector("[data-data-table-target='noResults']")
      if (emptyState) emptyState.hidden = remainingRows.length > 0
    }

    const editPanel = this.element.querySelector("[data-edit-member-panel-target='panel']")
    if (editPanel && !editPanel.hidden) {
      const editPanelController = this.application.getControllerForElementAndIdentifier(this.element, "edit-member-panel")
      editPanelController?.close()
    }
  }

  updateMemberCount(delta) {
    const countElement = this.element.querySelector(".hub-team-count")
    if (!countElement) return

    const currentCount = parseInt(countElement.textContent, 10) || 0
    countElement.textContent = Math.max(0, currentCount + delta)
  }
}
